import React, { useState, useEffect } from 'react';
import {
  adminGetInquiries,
  adminUpdateInquiryStatus,
  adminDeleteInquiry,
  adminSetInquiryFollowUp,
  adminClearInquiryFollowUp,
  adminMarkFollowUpDone,
  adminGetInquiryNotes,
  adminAddInquiryNote,
  adminDeleteInquiryNote,
} from '../../api/admin';
import AdminLayout from './AdminLayout';
import './AdminInquiries.css';
// The `status` column in the database is an ENUM restricted to these exact
// lowercase_underscore values — this is what gets sent to/received from the API.
const STATUS_OPTIONS = ['new', 'contacted', 'quotation_sent', 'in_progress', 'completed', 'closed'];

// Nice display labels shown to the admin, mapped from the enum value.
const STATUS_LABELS = {
  new:             'New',
  contacted:       'Contacted',
  quotation_sent:  'Quotation Sent',
  in_progress:     'In Progress',
  completed:       'Completed',
  closed:          'Closed',
};

const STATUS_COLORS = {
  new:             { bg: '#dbeafe', text: '#1d4ed8' },
  contacted:       { bg: '#fef3c7', text: '#92400e' },
  quotation_sent:  { bg: '#ede9fe', text: '#6d28d9' },
  in_progress:     { bg: '#ffedd5', text: '#c2410c' },
  completed:       { bg: '#dcfce7', text: '#16a34a' },
  closed:          { bg: '#f3f4f6', text: '#6b7280' },
};

// Convert a DB datetime ('YYYY-MM-DD HH:MM:SS') into the value a
// <input type="datetime-local"> needs ('YYYY-MM-DDTHH:MM')
const toDatetimeLocal = (dbValue) => {
  if (!dbValue) return '';
  return dbValue.replace(' ', 'T').slice(0, 16);
};

const followUpBadge = (inq) => {
  if (!inq.follow_up_at || Number(inq.follow_up_done) === 1) return null;
  const t = new Date(inq.follow_up_at.replace(' ', 'T')).getTime();
  const now = Date.now();
  const label = new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  if (t <= now) return <span className="adm-followup-badge adm-followup-badge--overdue">⏰ Overdue · {label}</span>;
  if (t - now <= 24 * 60 * 60 * 1000) return <span className="adm-followup-badge adm-followup-badge--today">🕑 {label}</span>;
  return <span className="adm-followup-badge adm-followup-badge--upcoming">📅 {label}</span>;
};

function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [selected, setSelected]   = useState(null);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const currentAdmin = JSON.parse(localStorage.getItem('admin_user') || 'null');

  const load = (openId) => {
    setLoading(true);
    adminGetInquiries().then(res => {
      if (res.success) {
        setInquiries(res.data);
        if (openId) {
          const match = res.data.find(i => String(i.id) === String(openId));
          if (match) openDetail(match);
        }
      }
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    // Support deep-linking from the reminder bell / popup toast: /admin/inquiries?open=123
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    load(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (inq) => {
    setSelected(inq);
    setFollowUpDate(toDatetimeLocal(inq.follow_up_at));
    setFollowUpNote(inq.follow_up_note || '');
    loadNotes(inq.id);
  };

  const loadNotes = (inquiryId) => {
    setNotesLoading(true);
    adminGetInquiryNotes(inquiryId)
      .then((res) => { if (res.success) setNotes(res.data || []); })
      .finally(() => setNotesLoading(false));
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    if (!currentAdmin?.id) { alert('Could not identify logged-in admin. Please log in again.'); return; }
    setSavingNote(true);
    try {
      await adminAddInquiryNote(selected.id, currentAdmin.id, newNote.trim());
      setNewNote('');
      loadNotes(selected.id);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await adminDeleteInquiryNote(noteId);
    loadNotes(selected.id);
  };

  const handleStatusChange = async (id, newStatus) => {
    await adminUpdateInquiryStatus(id, newStatus);
    load();
    if (selected?.id === id) setSelected({ ...selected, status: newStatus });
  };

  const handleSaveFollowUp = async () => {
    if (!followUpDate) return;
    setSavingFollowUp(true);
    try {
      await adminSetInquiryFollowUp(selected.id, followUpDate, followUpNote);
      load();
      setSelected({ ...selected, follow_up_at: followUpDate.replace('T', ' ') + ':00', follow_up_note: followUpNote, follow_up_done: 0 });
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleClearFollowUp = async () => {
    setSavingFollowUp(true);
    try {
      await adminClearInquiryFollowUp(selected.id);
      load();
      setFollowUpDate('');
      setFollowUpNote('');
      setSelected({ ...selected, follow_up_at: null, follow_up_note: null });
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleMarkFollowUpDone = async () => {
    setSavingFollowUp(true);
    try {
      await adminMarkFollowUpDone(selected.id);
      load();
      setSelected({ ...selected, follow_up_done: 1 });
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry permanently? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await adminDeleteInquiry(id);
      if (selected?.id === id) closeDetail();
      load();
    } finally {
      setDeleting(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    // Clean up ?open=ID from the URL if it was used to deep-link here
    if (window.location.search.includes('open=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const filtered = filter === 'all'
    ? inquiries
    : inquiries.filter(i => (i.status || 'new').toLowerCase() === filter.toLowerCase());

  const countByStatus = (s) => inquiries.filter(i => (i.status || 'new').toLowerCase() === s.toLowerCase()).length;

  const getStatusStyle = (status) => STATUS_COLORS[status] || STATUS_COLORS['new'];

  return (
    <AdminLayout title="Inquiries">
      <div className="adm-page-header">
        <h2>Customer Inquiries</h2>
      </div>

      {/* Status filter pills */}
      <div className="adm-gallery-filters" style={{marginBottom: '1.5rem'}}>
        <button className={`adm-gallery-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All ({inquiries.length})
        </button>
        {STATUS_OPTIONS.map(s => (
          <button
            key={s}
            className={`adm-gallery-filter-btn ${filter === s ? 'active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {STATUS_LABELS[s]} ({countByStatus(s)})
          </button>
        ))}
      </div>

      <div className="adm-section">
        {loading ? <div className="adm-loading">Loading...</div> : (
          filtered.length === 0 ? <p className="adm-empty">No inquiries found.</p> : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Inquiry No</th><th>Name</th><th>Phone</th><th>Email</th>
                    <th>Type</th><th>Status</th><th>Follow-up</th><th>Date</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inq => {
                    const style = getStatusStyle(inq.status || 'new');
                    return (
                      <tr key={inq.id}>
                        <td><code>{inq.inquiry_no}</code></td>
                        <td><strong>{inq.name}</strong></td>
                        <td><a href={`tel:${inq.phone}`}>{inq.phone}</a></td>
                        <td>{inq.email ? <a href={`mailto:${inq.email}`}>{inq.email}</a> : '—'}</td>
                        <td className="adm-capitalize">{inq.inquiry_type}</td>
                        <td>
                          <select
                            value={inq.status || 'new'}
                            onChange={(e) => handleStatusChange(inq.id, e.target.value)}
                            className="adm-status-select"
                            style={{ background: style.bg, color: style.text }}
                          >
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                          </select>
                        </td>
                        <td>{followUpBadge(inq) || <span className="adm-followup-none">—</span>}</td>
                        <td>{new Date(inq.created_at).toLocaleDateString('en-IN')}</td>
                        <td>
                          <button className="adm-btn-edit" onClick={() => openDetail(inq)}>👁️ View</button>
                          <button
                            className="adm-btn-delete"
                            onClick={() => handleDelete(inq.id)}
                            disabled={deleting}
                            title="Delete inquiry"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="adm-modal-overlay" onClick={closeDetail}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>Inquiry Details</h3>
              <button onClick={closeDetail}>✕</button>
            </div>
            <div className="adm-modal__body">
              <div className="adm-detail-row">
                <span className="adm-detail-label">Inquiry No</span>
                <span><code>{selected.inquiry_no}</code></span>
              </div>
              <div className="adm-detail-row">
                <span className="adm-detail-label">Name</span>
                <span>{selected.name}</span>
              </div>
              <div className="adm-detail-row">
                <span className="adm-detail-label">Phone</span>
                <span><a href={`tel:${selected.phone}`}>{selected.phone}</a></span>
              </div>
              {selected.email && (
                <div className="adm-detail-row">
                  <span className="adm-detail-label">Email</span>
                  <span><a href={`mailto:${selected.email}`}>{selected.email}</a></span>
                </div>
              )}
              <div className="adm-detail-row">
                <span className="adm-detail-label">Type</span>
                <span className="adm-capitalize">{selected.inquiry_type}</span>
              </div>
              {selected.subject && (
                <div className="adm-detail-row">
                  <span className="adm-detail-label">Subject</span>
                  <span>{selected.subject}</span>
                </div>
              )}
              <div className="adm-detail-row adm-detail-row--full">
                <span className="adm-detail-label">Message</span>
                <p className="adm-detail-message">{selected.message}</p>
              </div>
              <div className="adm-detail-row">
                <span className="adm-detail-label">Date</span>
                <span>{new Date(selected.created_at).toLocaleString('en-IN')}</span>
              </div>
              <div className="adm-detail-row">
                <span className="adm-detail-label">Status</span>
                <select
                  value={selected.status || 'new'}
                  onChange={(e) => handleStatusChange(selected.id, e.target.value)}
                  className="adm-status-select"
                >
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>

              <div className="adm-quick-call">
                <a href={`tel:${selected.phone}`} className="adm-btn-primary">📞 Call Now</a>
                <a href={`https://wa.me/91${selected.phone.replace(/\D/g,'').slice(-10)}`} target="_blank" rel="noreferrer" className="adm-btn-secondary">
                  💬 WhatsApp
                </a>
              </div>

              {/* ─── Follow-up / Reminder Scheduling ───────────────── */}
              <div className="adm-followup-section">
                <div className="adm-detail-label" style={{ marginBottom: '0.6rem' }}>Follow-up Reminder</div>

                {selected.follow_up_at && Number(selected.follow_up_done) !== 1 && (
                  <div className="adm-followup-current">
                    {followUpBadge(selected)}
                    {selected.follow_up_note && <p className="adm-followup-current__note">{selected.follow_up_note}</p>}
                    <button className="adm-btn-secondary" style={{ marginTop: '0.6rem' }} onClick={handleMarkFollowUpDone} disabled={savingFollowUp}>
                      ✔ Mark Done
                    </button>
                  </div>
                )}

                {selected.follow_up_at && Number(selected.follow_up_done) === 1 && (
                  <div className="adm-followup-current adm-followup-current--done">
                    ✔ Follow-up completed
                  </div>
                )}

                <div className="adm-followup-form">
                  <label className="adm-followup-form__label">
                    {selected.follow_up_at ? 'Reschedule to' : 'Schedule follow-up for'}
                  </label>
                  <input
                    type="datetime-local"
                    className="adm-followup-input"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                  />
                  <input
                    type="text"
                    className="adm-followup-input"
                    placeholder="Note (e.g. Call about site visit quote)"
                    value={followUpNote}
                    onChange={(e) => setFollowUpNote(e.target.value)}
                  />
                  <div className="adm-followup-form__actions">
                    <button
                      className="adm-btn-primary"
                      onClick={handleSaveFollowUp}
                      disabled={!followUpDate || savingFollowUp}
                    >
                      {savingFollowUp ? 'Saving...' : (selected.follow_up_at ? '🔁 Reschedule' : '⏰ Set Reminder')}
                    </button>
                    {selected.follow_up_at && (
                      <button className="adm-btn-clear" onClick={handleClearFollowUp} disabled={savingFollowUp}>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ─── Internal Notes / Activity Log ─────────────────── */}
              <div className="adm-notes-section">
                <div className="adm-detail-label" style={{ marginBottom: '0.6rem' }}>Internal Notes</div>

                <div className="adm-notes-list">
                  {notesLoading ? (
                    <p className="adm-notes-empty">Loading notes...</p>
                  ) : notes.length === 0 ? (
                    <p className="adm-notes-empty">No notes yet. Add one below.</p>
                  ) : (
                    notes.map((n) => (
                      <div key={n.id} className="adm-note-item">
                        <div className="adm-note-item__top">
                          <span className="adm-note-item__author">{n.admin_name || 'Admin'}</span>
                          <span className="adm-note-item__date">
                            {new Date(n.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <button className="adm-note-item__delete" onClick={() => handleDeleteNote(n.id)} title="Delete note">✕</button>
                        </div>
                        <p className="adm-note-item__text">{n.note}</p>
                      </div>
                    ))
                  )}
                </div>

                <div className="adm-note-form">
                  <textarea
                    className="adm-followup-input adm-note-textarea"
                    placeholder="Add a note (e.g. Called at 3pm, no answer, retry tomorrow)"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={2}
                  />
                  <button
                    className="adm-btn-primary"
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || savingNote}
                  >
                    {savingNote ? 'Saving...' : '📝 Add Note'}
                  </button>
                </div>
              </div>
            </div>
            <div className="adm-modal__footer adm-modal__footer--split">
              <button
                className="adm-btn-delete-full"
                onClick={() => handleDelete(selected.id)}
                disabled={deleting}
              >
                🗑️ Delete Inquiry
              </button>
              <button className="adm-btn-secondary" onClick={closeDetail}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminInquiries;